import { useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Icon, makeSlot, SlotProvider, Spacer, useSlots, useWidthBreakpoint, type IconType } from 'ht-sdk/react';

interface DashboardLayoutProps {
	children: ReactNode;
	largeBreakpoint?: number;
	navbarHeight?: number;
	menuIcon?: IconType;
}

export function DashboardLayout({ children, largeBreakpoint = 768, navbarHeight = 56, menuIcon }: DashboardLayoutProps) {
	const slots = useSlots<'NavItemsTop' | 'NavItemsBottom' | 'ProfileNavItem'>();

	const isLarge = useWidthBreakpoint(largeBreakpoint);

	// TODO: use some less hacky method of doing this like a useLocalStorage() hook or something
	const [navExpanded, setNavExpanded] = useState(localStorage.getItem('nav-expanded') === 'true');

	const navbarRef = useRef<HTMLElement>(null);

	const toggleNav = () => {
		const navbar = navbarRef.current;
		if (!navbar) return;

		const fromW = navbar.clientWidth;
		const fromH = navbar.clientHeight;
		const nextExpanded = !navExpanded;

		if (nextExpanded) {
			navbar.setAttribute('data-expanded', '');
		} else {
			navbar.removeAttribute('data-expanded');
		}
		localStorage.setItem('nav-expanded', '' + nextExpanded);
		setNavExpanded(nextExpanded);

		const toW = navbar.clientWidth;
		const toH = navbar.clientHeight;
		navbar.animate(
			[
				{
					width: fromW + 'px',
					height: fromH + 'px',
					overflow: 'hidden',
				},
				{
					width: toW + 'px',
					height: toH + 'px',
					overflow: 'hidden',
				},
			],
			{
				easing: 'ease-in-out',
				duration: isLarge ? 80 : 200,
			},
		);
	};

	return (
		<SlotProvider value={slots}>
			<div
				style={{ '--navbar-height': navbarHeight + 'px' } as CSSProperties}
				className="relative flex h-screen flex-col bg-gray-100 text-gray-800 md:flex-row dark:bg-gray-900 dark:text-gray-300"
			>
				<nav
					nav-bar="true"
					ref={navbarRef}
					data-expanded={navExpanded ? '' : undefined}
					className="group/nav fixed z-10 flex w-full flex-col rounded-b-sm bg-white p-2 shadow-lg md:relative md:h-full md:w-auto md:rounded-r-xl dark:bg-gray-800"
				>
					<div className="flex flex-row">
						<button
							onClick={toggleNav}
							className="mr-auto flex w-full rounded-md p-2 text-gray-900 hover:bg-gray-100 md:mb-3 dark:text-gray-100 dark:hover:bg-gray-700"
						>
							<Icon className="pointer-events-none size-5" icon={menuIcon} />
						</button>
						{!isLarge && slots.ProfileNavItem}
					</div>
					<div
						className="hidden flex-1 shrink flex-col group-data-[expanded]/nav:flex md:flex"
						onClick={!isLarge ? toggleNav : undefined}
					>
						{slots.NavItemsTop}
						<Spacer />
						{isLarge && slots.ProfileNavItem}
						{slots.NavItemsBottom}
					</div>
				</nav>
				<div className="mt-[var(--navbar-height)] flex min-w-0 flex-1 shrink flex-col md:mt-0">
					<div className="flex h-full flex-1 shrink overflow-auto">{children}</div>
				</div>
			</div>
		</SlotProvider>
	);
}
DashboardLayout.NavItemsTop = makeSlot('NavItemsTop');
DashboardLayout.NavItemsBottom = makeSlot('NavItemsBottom');
DashboardLayout.ProfileNavItem = makeSlot('ProfileNavItem');

export default DashboardLayout;
